import { A, useLocation, useNavigate } from "@solidjs/router";
import type { ProjectId } from "@storytell/types";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import type { IconTypes } from "solid-icons";
import {
  TbArrowLeft,
  TbArrowRight,
  TbBrain,
  TbBuildingCommunity,
  TbChartHistogram,
  TbCheck,
  TbChevronDown,
  TbCreditCard,
  TbCube,
  TbDeviceMobile,
  TbHistory,
  TbKey,
  TbMail,
  TbPlugConnected,
  TbSettings,
  TbShieldCheck,
  TbSparkles,
  TbUser,
  TbUsers,
} from "solid-icons/tb";
import { For, type ParentComponent, Show } from "solid-js";
import { useOrgScope } from "~/hooks/useOrgScope";
import { useI18n } from "~/i18n/context";
import type { TranslationKey } from "~/i18n/translations";
import { useWire } from "~/wire";

/**
 * Settings as a mode of the sidebar rather than a shell of its own: the logo
 * row, collapse control, resize handle and profile footer keep rendering, and
 * only the column between them swaps for the navigation over personal settings
 * and the organization's.
 */

interface NavItem {
  href: string;
  labelKey: TranslationKey;
  icon: IconTypes;
}

const PERSONAL_ITEMS: ReadonlyArray<NavItem> = [
  { href: "/settings/general", labelKey: "settings.general", icon: TbSettings },
  { href: "/settings/memory", labelKey: "settings.memory", icon: TbBrain },
  { href: "/settings/notifications", labelKey: "settings.notifications", icon: TbMail },
  { href: "/settings/devices", labelKey: "settings.devices", icon: TbDeviceMobile },
  { href: "/settings/tokens", labelKey: "settings.tokens", icon: TbKey },
  { href: "/settings/integrations", labelKey: "settings.integrations", icon: TbPlugConnected },
  { href: "/settings/tool-permissions", labelKey: "settings.tool_permissions", icon: TbShieldCheck },
];

// The projects page carries the organization's name, so there is no separate
// general row.
const OWNER_ITEMS: ReadonlyArray<{ segment: string; labelKey: TranslationKey; icon: IconTypes }> = [
  { segment: "projects", labelKey: "organization.nav_projects", icon: TbCube },
  { segment: "members", labelKey: "organization.nav_members", icon: TbUsers },
  { segment: "memory", labelKey: "organization.nav_memory", icon: TbBrain },
  { segment: "billing", labelKey: "organization.nav_billing", icon: TbCreditCard },
  { segment: "usage", labelKey: "organization.nav_usage", icon: TbChartHistogram },
  { segment: "integrations", labelKey: "organization.nav_integrations", icon: TbPlugConnected },
  { segment: "ai-providers", labelKey: "organization.nav_ai_providers", icon: TbSparkles },
  { segment: "audit", labelKey: "organization.nav_audit", icon: TbHistory },
];

const SETTINGS_PATTERNS = [/^\/settings(\/|$)/, /^\/orgs\/[^/]+(\/.*)?$/];

/** Routes that put the sidebar into settings mode and the content column into the settings page frame. */
export const isSettingsPath = (pathname: string): boolean =>
  SETTINGS_PATTERNS.some((pattern) => pattern.test(pathname));

const NAV_LINK =
  "flex items-center gap-2.5 px-4 py-2 text-sm rounded-lg text-secondary-text hover:text-primary-text hover:bg-canvas transition-colors";

const NAV_LINK_ACTIVE = "bg-panel-hover text-primary-text font-medium";

// Shares the nav items' geometry so the rail reads as one column; only the
// muted colour and the arrow nudge mark these as the way out rather than a
// destination.
const BACK_LINK = `${NAV_LINK} group text-muted-text`;

// Personal and organization settings are separate navs; each ends with the one
// row that crosses to the other, so neither needs a trip through the popover.
const CROSS_LINK = `${NAV_LINK} group mt-auto text-muted-text`;

const RAIL_LINK =
  "flex w-full items-center justify-center rounded-md px-2 py-1.5 text-secondary-text transition-colors hover:bg-canvas hover:text-primary-text";

const RAIL_LINK_ACTIVE = "bg-panel-hover text-primary-text";

// The whole /orgs subtree is owners-only, so an organization is either linkable
// here or absent — there is no member landing to fall back to.
const orgHome = (organizationId: string) => `/orgs/${organizationId}/projects`;

/** Restores the project the user was working in, which the logo's plain link home does not. */
const useBackToApp = () => {
  const wire = useWire();
  const navigate = useNavigate();

  return () => {
    const [recent] = wire.userContext.getRecentProjectIds();
    if (recent) wire.userContext.switchToProject(recent as ProjectId);
    else navigate("/");
  };
};

export const SettingsNavExpanded = () => {
  const { t } = useI18n();
  const location = useLocation();
  const scope = useOrgScope();
  const navigate = useNavigate();
  const backToApp = useBackToApp();

  const isOrgRoute = () => location.pathname.startsWith("/orgs/");

  return (
    <div class="scrollbar-show-on-hover flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
      <button type="button" onClick={backToApp} class={`${BACK_LINK} mt-1 mb-2.5`}>
        <TbArrowLeft class="size-4 transition-transform group-hover:-translate-x-0.5" />
        {t("settings_nav.back_to_app")}
      </button>

      <Show
        when={isOrgRoute()}
        fallback={
          <>
            <For each={PERSONAL_ITEMS}>
              {(item) => (
                <A href={item.href} class={NAV_LINK} activeClass={NAV_LINK_ACTIVE}>
                  <item.icon class="size-4" />
                  {t(item.labelKey)}
                </A>
              )}
            </For>
            <Show when={scope.defaultOwnedOrganization()}>
              {(tenant) => (
                <A href={orgHome(tenant().organizationId)} class={CROSS_LINK}>
                  <TbBuildingCommunity class="size-4" />
                  <span class="min-w-0 flex-1 truncate">{t("settings_nav.organization_settings")}</span>
                  <TbArrowRight class="size-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                </A>
              )}
            </Show>
          </>
        }
      >
        <Show when={scope.currentOrganization()}>
          {(current) => (
            <>
              {/* Only the owned organizations are offered: the switcher navigates
                  into /orgs, and the rest have no page there to reach. */}
              <Show
                when={scope.ownedOrganizations().length > 1}
                fallback={
                  <span class="flex items-center gap-2.5 px-4 py-2 text-sm text-primary-text">
                    <TbBuildingCommunity class="size-4 shrink-0 text-muted-text" />
                    <span class="truncate">{scope.organizationName(current())}</span>
                  </span>
                }
              >
                <StDropdown
                  placement="bottom-start"
                  gutter={4}
                  contentProps={{ class: "z-50 w-[var(--kb-popper-anchor-width)]" }}
                  content={
                    <For each={scope.ownedOrganizations()}>
                      {(tenant) => (
                        // Switching organization stays inside settings, so this
                        // navigates rather than moving the working project scope.
                        <StDropdownItem
                          onClick={() => navigate(orgHome(tenant.organizationId))}
                          aria-current={tenant.organizationId === current().organizationId || undefined}
                          class="group flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-primary-text transition-colors hover:bg-canvas-hover"
                        >
                          <TbCheck
                            class="size-4 shrink-0"
                            classList={{ invisible: tenant.organizationId !== current().organizationId }}
                          />
                          <span class="min-w-0 flex-1">
                            <span class="block truncate text-sm">{scope.organizationName(tenant)}</span>
                            <span class="block truncate text-xs text-muted-text">
                              {scope.organizationRoleLabel(tenant)}
                            </span>
                          </span>
                        </StDropdownItem>
                      )}
                    </For>
                  }
                >
                  <StDropdownTrigger
                    as="button"
                    type="button"
                    aria-label={t("organization.switcher.aria_label")}
                    class="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-4 py-2 text-left text-sm text-primary-text transition-colors hover:bg-canvas"
                  >
                    <TbBuildingCommunity class="size-4 shrink-0 text-muted-text" />
                    <span class="min-w-0 flex-1 truncate">{scope.organizationName(current())}</span>
                    <TbChevronDown class="size-3.5 shrink-0 text-muted-text" />
                  </StDropdownTrigger>
                </StDropdown>
              </Show>

              <Show when={scope.ownedCurrentOrgId()}>
                {(orgId) => (
                  <For each={OWNER_ITEMS}>
                    {(item) => (
                      <A href={`/orgs/${orgId()}/${item.segment}`} class={NAV_LINK} activeClass={NAV_LINK_ACTIVE}>
                        <item.icon class="size-4" />
                        {t(item.labelKey)}
                      </A>
                    )}
                  </For>
                )}
              </Show>

              <A href="/settings/general" class={CROSS_LINK}>
                <TbUser class="size-4" />
                <span class="min-w-0 flex-1 truncate">{t("settings_nav.personal_settings")}</span>
                <TbArrowRight class="size-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
              </A>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
};

export const SettingsNavCollapsed = () => {
  const { t } = useI18n();
  const location = useLocation();
  const scope = useOrgScope();
  const backToApp = useBackToApp();

  const isOrgRoute = () => location.pathname.startsWith("/orgs/");

  return (
    <div class="scrollbar-show-on-hover flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
      <StTooltip content={t("settings_nav.back_to_app")} placement="right">
        <StTooltipTrigger
          as="button"
          type="button"
          onClick={backToApp}
          aria-label={t("settings_nav.back_to_app")}
          class={`${RAIL_LINK} mt-1 mb-2 cursor-pointer`}
        >
          <TbArrowLeft class="size-4" />
        </StTooltipTrigger>
      </StTooltip>

      <Show
        when={isOrgRoute()}
        fallback={
          <>
            <For each={PERSONAL_ITEMS}>
              {(item) => (
                <StTooltip content={t(item.labelKey)} placement="right">
                  <StTooltipTrigger as="div" class="w-full">
                    <A href={item.href} class={RAIL_LINK} activeClass={RAIL_LINK_ACTIVE} aria-label={t(item.labelKey)}>
                      <item.icon class="size-4" />
                    </A>
                  </StTooltipTrigger>
                </StTooltip>
              )}
            </For>
            <Show when={scope.defaultOwnedOrganization()}>
              {(tenant) => (
                <StTooltip content={t("settings_nav.organization_settings")} placement="right">
                  <StTooltipTrigger as="div" class="mt-auto w-full">
                    <A
                      href={orgHome(tenant().organizationId)}
                      class={RAIL_LINK}
                      aria-label={t("settings_nav.organization_settings")}
                    >
                      <TbBuildingCommunity class="size-4" />
                    </A>
                  </StTooltipTrigger>
                </StTooltip>
              )}
            </Show>
          </>
        }
      >
        <>
          <Show when={scope.ownedCurrentOrgId()}>
            {(orgId) => (
              <For each={OWNER_ITEMS}>
                {(item) => (
                  <StTooltip content={t(item.labelKey)} placement="right">
                    <StTooltipTrigger as="div" class="w-full">
                      <A
                        href={`/orgs/${orgId()}/${item.segment}`}
                        class={RAIL_LINK}
                        activeClass={RAIL_LINK_ACTIVE}
                        aria-label={t(item.labelKey)}
                      >
                        <item.icon class="size-4" />
                      </A>
                    </StTooltipTrigger>
                  </StTooltip>
                )}
              </For>
            )}
          </Show>

          <StTooltip content={t("settings_nav.personal_settings")} placement="right">
            <StTooltipTrigger as="div" class="mt-auto w-full">
              <A href="/settings/general" class={RAIL_LINK} aria-label={t("settings_nav.personal_settings")}>
                <TbUser class="size-4" />
              </A>
            </StTooltipTrigger>
          </StTooltip>
        </>
      </Show>
    </div>
  );
};

/**
 * The settings page frame. The organization family addresses one organization
 * out of several a user may hold, so the page names it rather than the generic
 * scope.
 */
export const SettingsContent: ParentComponent = (props) => {
  const { t } = useI18n();
  const location = useLocation();
  const scope = useOrgScope();

  const pageHeading = () => {
    if (!location.pathname.startsWith("/orgs/")) return t("settings.title");
    const current = scope.currentOrganization();
    return current ? scope.organizationName(current) : t("organization.title");
  };

  return (
    <div class="mx-auto max-w-5xl px-6 py-8">
      <h1 class="mb-8 text-2xl font-semibold text-primary-text">{pageHeading()}</h1>
      {props.children}
    </div>
  );
};
